import { useEffect, useRef, useState } from "react";
import { PALETTE as P } from "../tokens";

// Guided breathing animation — the single breathing moment that opens both
// daily rituals (the Morning Check-in's intro overlay and the Evening
// Wind-Down's Breathing Reset). It brings the user back into their body
// before any questions or activities.
//
// ONE cadence, both surfaces (July 14 2026 — locked-in product decision):
// inhale 4s → exhale 6s, no hold. The circle runs ONE continuous
// keyframes cycle (inhale + exhale as percentage segments of a single
// animation) instead of restarting a keyed animation per phase — that
// restart seam is what read as "choppy". The phase label is JS-timed off
// the same constants so copy and motion stay in sync.
//
// Two modes share that single cadence:
//
// LOOPING (default — the morning intro): the cycle repeats until the
// caller advances.
//
// ONCE (the Evening Wind-Down): the same cycle plays exactly one time,
// then the circle settles and stays still. F&F: the endless loop made
// the step feel unfinished; one deliberate round is the moment, then
// it's done.
export const INHALE_MS = 4000; // 4s in
export const EXHALE_MS = 6000; // 6s out
export const CYCLE_MS = INHALE_MS + EXHALE_MS;

const PHASES = [
  { key: "inhale", label: "Breathe in" },
  { key: "exhale", label: "Breathe out" },
];

const ONCE_PHASES = [
  { key: "inhale", label: "Breathe in" },
  { key: "exhale", label: "Breathe out" },
  { key: "done", label: "Whenever you're ready" },
];

// Keyframe stops as percentages.
const INHALE_END = ((INHALE_MS / CYCLE_MS) * 100).toFixed(3);
const EASE = "cubic-bezier(0.45, 0, 0.35, 1)";

// Props:
//   once             — play exactly ONE 4/6 cycle, then stop still.
//                      onCyclesComplete fires when the round ends.
//   suggestedCycles  — (looping mode) the guided cycle count shown in the
//                      counter; also the number of full cycles before
//                      onCyclesComplete
//   onCyclesComplete — fired ONCE after suggestedCycles full cycles (the
//                      morning intro auto-advances on it) — or, in once
//                      mode, when the single round completes
//   showCounter      — hide the cycle line entirely (morning overlay)
export default function BreathingGuide({
  once = false,
  suggestedCycles = 3,
  onCyclesComplete,
  showCounter = true,
}) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycle, setCycle] = useState(1);

  // Refs so the timer chain (deliberately mounted once) always sees the
  // latest callback/count without restarting mid-breath.
  const onCompleteRef = useRef(onCyclesComplete);
  const targetRef = useRef(suggestedCycles);
  useEffect(() => {
    onCompleteRef.current = onCyclesComplete;
    targetRef.current = suggestedCycles;
  });

  useEffect(() => {
    let timers = [];
    if (once) {
      // One round, then still. No recursion — the round is the ritual.
      timers = [
        setTimeout(() => setPhaseIdx(1), INHALE_MS),
        setTimeout(() => {
          setPhaseIdx(2);
          onCompleteRef.current?.();
        }, CYCLE_MS),
      ];
      return () => timers.forEach(clearTimeout);
    }
    let completed = 0;
    let fired = false;
    const runCycle = () => {
      setPhaseIdx(0);
      timers = [
        setTimeout(() => setPhaseIdx(1), INHALE_MS),
        setTimeout(() => {
          completed += 1;
          if (!fired && completed >= targetRef.current) {
            fired = true;
            onCompleteRef.current?.();
          }
          setCycle((c) => c + 1);
          runCycle();
        }, CYCLE_MS),
      ];
    };
    runCycle();
    return () => timers.forEach(clearTimeout);
  }, [once]);

  const phase = (once ? ONCE_PHASES : PHASES)[phaseIdx];

  // Same keyframes either way — once mode just plays them a single time
  // and holds the resting end state.
  const iteration = once ? "1 forwards" : "infinite";
  const circleAnimation = `breath-cycle ${CYCLE_MS}ms ${iteration}`;
  const glowAnimation = `breath-cycle-glow ${CYCLE_MS}ms ${iteration}`;

  return (
    <>
      <style>{`
        /* One fluid cycle — expand ${INHALE_MS / 1000}s → contract ${EXHALE_MS / 1000}s */
        @keyframes breath-cycle {
          0% { transform: scale(1); animation-timing-function: ${EASE}; }
          ${INHALE_END}% { transform: scale(1.4); animation-timing-function: ${EASE}; }
          100% { transform: scale(1); }
        }
        @keyframes breath-cycle-glow {
          0% { transform: scale(1.15); animation-timing-function: ${EASE}; }
          ${INHALE_END}% { transform: scale(1.6); animation-timing-function: ${EASE}; }
          100% { transform: scale(1.15); }
        }
        @keyframes breath-label {
          0% { opacity: 0; transform: translateY(4px); }
          25% { opacity: 1; transform: translateY(0); }
          100% { opacity: 1; }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        aria-label={phase.label}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 30, padding: "10px 0 6px",
        }}
      >
        {/* Gold breathing circle — same visual language as the rest of Haiven */}
        <div style={{
          position: "relative", width: 200, height: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            background: `radial-gradient(circle, ${P.primarySoft} 0%, transparent 70%)`,
            animation: glowAnimation,
          }} />
          <div style={{
            width: 150, height: 150, borderRadius: "50%",
            border: `1.5px solid ${P.primary}`,
            background: `radial-gradient(circle, ${P.primarySoft} 0%, rgba(158,132,86,0.02) 80%)`,
            animation: circleAnimation,
            boxShadow: "0 0 40px rgba(158,132,86,0.14)",
          }} />
        </div>

        <div
          key={phase.key + cycle}
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: "italic", fontWeight: 400,
            fontSize: phase.key === "done" ? 20 : 28,
            color: P.primary, letterSpacing: "0.01em", textAlign: "center",
            animation: "breath-label 0.6s ease both",
          }}
        >
          {phase.label}
        </div>

        {showCounter && !once && (
          <div style={{
            fontSize: 11, color: P.textMute, letterSpacing: "0.18em",
            textTransform: "uppercase", fontWeight: 500,
          }}>
            {cycle <= suggestedCycles
              ? `Cycle ${cycle} of ${suggestedCycles}`
              : "Whenever you're ready"}
          </div>
        )}
      </div>
    </>
  );
}
